/**
 * VK ID sign-in, web redirect flow.
 *
 * We generate the PKCE pair and `state` ourselves (as the Android app does)
 * and keep them in localStorage: VK returns only `code`, `state` and
 * `device_id` to /auth/vk/callback, and the API exchanges the code with VK
 * using the verifier and the app secret. The browser never sees VK tokens.
 *
 * localStorage, not sessionStorage: on Android, VK ID hands off to the VK
 * app, which sends the visitor back into a NEW browser tab. sessionStorage is
 * per tab, so the callback found nothing and every such sign-in was rejected
 * (Sentry WEB-APP-1B). localStorage is shared by the origin's tabs; the TTL
 * keeps an abandoned round-trip from matching a much later callback.
 */

/** VK ID web app (id.vk.ru/business). Public, same for every environment. */
const VK_ID_WEB_APP_ID = 54806331;

export const VK_CALLBACK_PATH = '/auth/vk/callback';

const VK_PKCE_KEY = 'vk_id_pkce';

/** VK's authorization code itself lives 10 minutes (`expires_in=600`). */
const VK_PKCE_TTL_MS = 10 * 60 * 1000;

interface VkPkceSession {
  state: string;
  codeVerifier: string;
  createdAt: number;
  /**
   * Adding VK ID to the signed-in account (ADR-001) instead of signing in.
   * Kept inside the state-bound session, not in the shared link flag, so it
   * survives the new tab too and cannot leak into an unrelated sign-in.
   */
  link: boolean;
}

export interface VkPkceResult {
  codeVerifier: string;
  link: boolean;
}

/** RFC 7636 unreserved charset; 64 chars sits inside the 43-128 range. */
function randomUrlSafe(length: number): string {
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

export function vkRedirectUri(): string {
  return `${window.location.origin}${VK_CALLBACK_PATH}`;
}

/** Leave for VK ID. Resolves only if the SDK fails before navigating away. */
export async function startVkIdLogin({ link = false } = {}): Promise<void> {
  const session: VkPkceSession = {
    state: randomUrlSafe(32),
    codeVerifier: randomUrlSafe(64),
    createdAt: Date.now(),
    link,
  };
  localStorage.setItem(VK_PKCE_KEY, JSON.stringify(session));

  // Loaded on click: the SDK is only needed by the few visitors who use it.
  const VKID = await import('@vkid/sdk');
  VKID.Config.init({
    app: VK_ID_WEB_APP_ID,
    redirectUrl: vkRedirectUri(),
    state: session.state,
    codeVerifier: session.codeVerifier,
    scope: 'email',
    responseMode: VKID.ConfigResponseMode.Redirect,
    mode: VKID.ConfigAuthMode.Redirect,
  });
  await VKID.Auth.login();
}

/**
 * Read and forget the PKCE session for the returning `state`. Null when the
 * browser has none, it expired, or the state differs (a forged or stale callback link), so the
 * code is never exchanged on behalf of a round-trip this tab did not start.
 */
export function consumeVkPkceSession(
  returnedState: string | null
): VkPkceResult | null {
  const raw = localStorage.getItem(VK_PKCE_KEY);
  localStorage.removeItem(VK_PKCE_KEY);
  if (!raw || !returnedState) {
    return null;
  }
  try {
    const session = JSON.parse(raw) as VkPkceSession;
    const fresh = Date.now() - session.createdAt < VK_PKCE_TTL_MS;
    return fresh && session.state === returnedState
      ? { codeVerifier: session.codeVerifier, link: session.link === true }
      : null;
  } catch {
    return null;
  }
}
